/**
 * TopBarC — Variant C top bar (chat-first redesign, Spec 29).
 *
 * Compact one-row chrome above the tab row. Shows:
 *   - Brand mark (left)
 *   - Mode pill (LIVE / SANDBOX) — colour-coded, interactive when
 *     safe to toggle
 *   - Palette button (right)
 *
 * # DECISION: pre-Spec-29 TopBar also carried doc title + framework
 * #   caption + a heartbeat strip. Those all retire.
 * # WHY: per writer feedback (2026-04-23) the UI was "overwhelming
 * #      with a lot of stuff." Doc title + framework caption are
 * #      redundant with the ContextStrip chips directly below (doc
 * #      type chip covers "CER", workspace chip covers the title).
 * #      The heartbeat strip was a decorative "run is live" signal;
 * #      that information now lives in the Spine tab (appears when
 * #      a run starts) and the inline timeline events. Eliminating
 * #      ~60 px of vertical chrome.
 * # DATE: 2026-04-23
 *
 * SRP: pure presentation. Receives data via props; emits intents
 * via callbacks. No reducer subscription, no fetches.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";
import TokenUsageDisplay from "./TokenUsageDisplay";
import { brand } from "../../config/brand";
import { openExternal } from "../services/openExternal";

// Trust Center — same verified-live marketing host used across the app.
// Opened via openExternal (system browser) so the Office task-pane WebView
// doesn't drop it, matching how every other external link here behaves.
const TRUST_URL = "https://asthra-writer.ai/trust";

export type TopBarMode = "live" | "sandbox";

interface TopBarCProps {
  /** Sandbox vs Live — drives pill colour + label. Reflects backend
   *  config (fetched via getAppConfig), not a hard-coded value. */
  mode: TopBarMode;
  // onPaletteOpen retired with the Command Palette (Spec 29
  // palette-merge, 2026-04-23). Typing "/" in the CommandRail is
  // the single command-menu trigger. Any ⇧⌘P / Commands button
  // would lie about the keybind (⇧⌘P is not wired to anything).
  /** Optional mode-flip intent. When provided, pill is a button;
   *  when omitted, pill is read-only (host omits the handler when
   *  toggling would be unsafe — mid-run / approval states). */
  onModeToggle?: () => void;
  /** True while a mode-flip request is in flight — disables + dims
   *  the pill to block double-clicks. */
  modeToggling?: boolean;
  /** Sign-out intent. Surfaces the existing api/client.logout()
   *  pathway (already wired via the /logout slash command) as a
   *  visible affordance. When omitted, the affordance is hidden —
   *  callers can choose surfaces where the action is meaningless
   *  (pre-login, embedded contexts) and not render it. */
  onLogout?: () => void;
  /** Open the Billing & usage surface (plan, portal, payment history). Rendered
   *  as a profile-menu item when provided. */
  onOpenBilling?: () => void;
  /** Open the Change password dialog. Rendered as a profile-menu item when
   *  provided. */
  onChangePassword?: () => void;
  /** Logged-in user's identifier (typically the email used at login).
   *  Rendered as a small mono label adjacent to the logout button so
   *  the writer can confirm WHICH account is about to be logged out.
   *  Null/empty hides the label — TopBar renders only the logout
   *  affordance. The shell sources this from App's session state via
   *  prop drilling (auth state SSOT lives in App, not duplicated). */
  userIdentifier?: string | null;
  /** Whether to render the header's internal/developer affordances.
   *
   *  Covers the LIVE/SANDBOX mode pill — a build mode end users have no
   *  reason to flip, flagged in pre-AppSource testing. The model-backend
   *  (LLM provider) label used to sit beside it; it was removed from the
   *  header outright (2026-10-16) to give the context chips the room.
   *
   *  Defaults to ``false`` so it is hidden unless a flag explicitly
   *  turns it on: ``useFlag`` returns false while flags are loading,
   *  on fetch error, and for unknown keys, so "show when true" is the
   *  only polarity that keeps them hidden in all three cases. */
  showInternalTools?: boolean;
  /** True when this bundle came from the Vite dev server (``pnpm dev``)
   *  rather than the compose/Docker ``pnpm build``. Relabels the mode pill
   *  "DEV" in place of "LIVE" so the two stacks are distinguishable at a
   *  glance. Sandbox mode keeps its own label — it is a distinct state, not
   *  a build. The shell sources this from ``config/buildEnv``; it is a prop
   *  so this component stays pure and tests can drive either label. */
  devBuild?: boolean;
  /** Live token counts accumulated during active run. When provided, the
   *  profile menu's token-usage item adds them to the persisted totals. */
  liveTokens?: {
    input: number;
    output: number;
    thinking: number;
  } | null;
  /** Context chips (workspace · doctype · library) rendered right beside
   *  the mode pill. The shell owns them and omits the slot during
   *  takeovers and on Home, exactly when the old chips row was hidden. */
  contextSlot?: React.ReactNode;
}

const useStyles = makeStyles({
  root: {
    position: "relative",
    padding: "10px 12px",
    background: surfaceColors.paper,
    borderBottom: `1px solid ${surfaceColors.line}`,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "nowrap",
    "@media (max-width: 520px)": {
      padding: "8px 12px",
      gap: "8px",
    },
  },
  // Left cluster (brand + LIVE pill + context chips). Allowed to shrink and
  // clip so it — not the right-side profile control — is what gives when the
  // task pane is narrow. ``minWidth: 0`` lets a flex item shrink below its
  // content width; ``overflow: hidden`` clips the excess instead of forcing the
  // row wider (which would push the profile button off the right edge).
  leftGroup: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexShrink: 1,
    minWidth: 0,
    overflow: "hidden",
    "@media (max-width: 520px)": {
      gap: "8px",
    },
  },
  brandMark: {
    fontFamily: fonts.display,
    fontSize: "14px",
    fontWeight: 700,
    color: brandColors.primary,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    opacity: 0.95,
    lineHeight: 1,
    whiteSpace: "nowrap",
    "@media (max-width: 520px)": {
      fontSize: "12px",
      letterSpacing: "0.3px",
    },
  },
  brandIcon: {
    width: "18px",
    height: "18px",
    flexShrink: 0,
  },
  wordmark: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    fontFamily: fonts.display,
    fontSize: "15px",
    fontWeight: 700,
    letterSpacing: "0.6px",
    color: inkColors.ink,
    lineHeight: 1,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  // Very narrow panes keep the spark only, so the library name stays readable.
  wordmarkText: {
    "@media (max-width: 400px)": {
      display: "none",
    },
  },
  spacer: {
    flex: 1,
    minWidth: 0,
  },
  // Gives way before the brand lockup: the chips ellipsise first.
  contextSlot: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    flexShrink: 100,
    overflow: "hidden",
  },
  modePill: {
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.6px",
    padding: "4px 9px",
    borderRadius: "6px",
    textTransform: "uppercase",
    fontFamily: fonts.body,
    border: "none",
    cursor: "default",
    lineHeight: 1,
    flexShrink: 0,
  },
  modePillClickable: {
    cursor: "pointer",
    ":hover": {
      filter: "brightness(0.95)",
    },
  },
  modePillBusy: {
    opacity: 0.6,
    cursor: "wait",
  },
  modePillLive: {
    color: "#15613A",
    background: "#CAF2DA",
  },
  modePillSandbox: {
    color: "#7C2D12",
    background: "#FED7AA",
  },
  // DEV build (Vite dev server). Blue rather than the LIVE green so a
  // writer never mistakes a local stack for production at a glance.
  modePillDev: {
    color: "#1E3A8A",
    background: "#DBEAFE",
  },
  profileButton: {
    fontSize: "13px",
    fontWeight: 600,
    fontFamily: fonts.body,
    color: inkColors.ink3,
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: "4px 6px",
    borderRadius: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    position: "relative",
    flexShrink: 0,
    ":hover": {
      color: brandColors.primary,
      background: surfaceColors.paperDim,
    },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "1px",
    },
  },
  dropdownContainer: {
    position: "absolute",
    top: "100%",
    right: "0",
    marginTop: "4px",
    background: "white",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
    zIndex: 1000,
    minWidth: "180px",
  },
  dropdownContent: {
    padding: "8px 0",
  },
  dropdownUserInfo: {
    padding: "8px 12px",
    fontSize: "11px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    borderBottom: `1px solid ${surfaceColors.line}`,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  dropdownLogoutBtn: {
    width: "100%",
    fontSize: "11px",
    fontWeight: 500,
    fontFamily: fonts.body,
    color: inkColors.ink3,
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: "8px 12px",
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    ":hover": {
      color: brandColors.primary,
      background: surfaceColors.paperDim,
    },
  },
  dropdownIcon: {
    fontSize: "11px",
    opacity: 0.8,
  },
  profileRoot: {
    position: "relative",
  },
});

export const TopBarC: React.FC<TopBarCProps> = ({
  mode,
  onModeToggle,
  modeToggling,
  onLogout,
  onOpenBilling,
  onChangePassword,
  userIdentifier,
  showInternalTools = false,
  devBuild = false,
  liveTokens,
  contextSlot,
}) => {
  const styles = useStyles();
  const pillClickable = !!onModeToggle && !modeToggling;
  // Label: "live" reads "dev" on a Vite dev-server bundle (pnpm dev) so the
  // local stack is distinguishable from the compose/Docker production build.
  // Sandbox keeps its own label — it is a mode, not a build.
  const pillLabel = mode === "live" && devBuild ? "dev" : mode;
  const buildHint = devBuild ? "DEV build (Vite dev server) — " : "";
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  return (
    <div className={styles.root}>
      {/* Left cluster — brand + internal-tools pills. Grouped so it shrinks and
          clips as one unit when the pane is narrow, keeping the right-side
          profile control on-screen (see styles.leftGroup). */}
      <div className={styles.leftGroup}>
        {/* Brands with a square mark + wordmark get the launch-film lockup
            (spark + "ASTHRA"); logo-only white labels keep their image. */}
        {brand.logoMark && brand.wordmark ? (
          <span className={styles.wordmark} aria-label={brand.name}>
            <img src={brand.logoMark} alt="" className={styles.brandIcon} />
            <span className={styles.wordmarkText}>{brand.wordmark}</span>
          </span>
        ) : (
          <span className={styles.brandMark}>
            <img src={brand.logoHorizontal} alt={brand.name} style={{ width: "100px", height: "auto" }} />
          </span>
        )}
        {/* LIVE/SANDBOX mode pill. Gated on ``show.internal-tools`` — see
            showInternalTools. Hiding it removes the only mode indicator as
            well as the toggle, which is the intent: production is expected
            to present one mode, not a switch between two. */}
        {showInternalTools && (
          <button
            type="button"
            onClick={pillClickable ? onModeToggle : undefined}
            disabled={!pillClickable}
            title={
              !onModeToggle
                ? `${buildHint}Mode flips disabled while a run is active`
                : modeToggling
                  ? `${buildHint}Switching mode…`
                  : `${buildHint}Click to switch to ${mode === "live" ? "sandbox" : "live"}`
            }
            className={mergeClasses(
              styles.modePill,
              mode === "live" ? (devBuild ? styles.modePillDev : styles.modePillLive) : styles.modePillSandbox,
              pillClickable && styles.modePillClickable,
              modeToggling && styles.modePillBusy,
            )}
          >
            {pillLabel}
          </button>
        )}
        {/* Context chips (workspace · doctype · library) sit right beside the
            mode pill; they shrink and ellipsise before the brand does. */}
        {contextSlot && <div className={styles.contextSlot}>{contextSlot}</div>}
      </div>
      {/* Spacer pushes the profile menu to the right edge. */}
      <span className={styles.spacer} />
      {/* Profile dropdown menu — shows user identity + logout action */}
      {(onLogout || userIdentifier || onOpenBilling) && (
        <div className={styles.profileRoot} ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className={styles.profileButton}
            title="User menu"
            aria-label="User menu"
            aria-expanded={dropdownOpen}
            aria-haspopup="true"
          >
            👤
          </button>
          {dropdownOpen && (
            <div className={styles.dropdownContainer} role="menu">
              <div className={styles.dropdownContent}>
                {userIdentifier && (
                  <div className={styles.dropdownUserInfo} title={userIdentifier}>
                    {userIdentifier}
                  </div>
                )}
                {/* Token usage — opens the input/output breakdown popover. */}
                <TokenUsageDisplay variant="menu" className={styles.dropdownLogoutBtn} liveTokens={liveTokens} />
                <button
                  type="button"
                  onClick={() => {
                    setDropdownOpen(false);
                    openExternal(TRUST_URL);
                  }}
                  className={styles.dropdownLogoutBtn}
                  role="menuitem"
                >
                  <span className={styles.dropdownIcon} aria-hidden="true">
                    🛡
                  </span>
                  <span>Trust Center</span>
                </button>
                {onOpenBilling && (
                  <button
                    type="button"
                    onClick={() => {
                      setDropdownOpen(false);
                      onOpenBilling();
                    }}
                    className={styles.dropdownLogoutBtn}
                    role="menuitem"
                  >
                    <span className={styles.dropdownIcon} aria-hidden="true">
                      💳
                    </span>
                    <span>Billing &amp; usage</span>
                  </button>
                )}
                {onChangePassword && (
                  <button
                    type="button"
                    onClick={() => {
                      setDropdownOpen(false);
                      onChangePassword();
                    }}
                    className={styles.dropdownLogoutBtn}
                    role="menuitem"
                  >
                    <span className={styles.dropdownIcon} aria-hidden="true">
                      🔑
                    </span>
                    <span>Change password</span>
                  </button>
                )}
                {onLogout && (
                  <button
                    type="button"
                    onClick={() => {
                      setDropdownOpen(false);
                      onLogout();
                    }}
                    className={styles.dropdownLogoutBtn}
                    role="menuitem"
                  >
                    <span className={styles.dropdownIcon} aria-hidden="true">
                      ⏻
                    </span>
                    <span>Log out</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TopBarC;
